import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Button, Form, Input, InputNumber, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { MenuOutlined } from '@ant-design/icons';
import { serviceBundler } from '../../../App';

import { PlusCircleOutlined } from '@ant-design/icons';
import { DbTranslationKeyValue } from '../../../Models/Language/DbTranslationKeyValue';

interface Item {
  key: string;
  index: number;
  languageKey: string;
  languageLabel: string;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  validationMessage: string;
  dataSource: DbTranslationKeyValue[];
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'number' | 'text';
  record: Item;
  index: number;
  children: React.ReactNode;
}

const checkLabelExist = (record: Item, errorString: string) => {
  return (alldata: any, value: string) => {
    var currentLanguage =
      serviceBundler.languageService.GetCurrentLanguage().keysLabels;
    var foundIndex = currentLanguage?.findIndex(
      (x) => x.languageKey.toLocaleLowerCase() === value.toLocaleLowerCase(),
    );
    if (
      foundIndex >= 0 &&
      foundIndex != record.index &&
      alldata.field !== 'languageLabel'
    ) {
      return Promise.reject(new Error(errorString));
    }
    return Promise.resolve();
  };
};

type DataIndex = keyof Item;

const EditableCell: React.FC<EditableCellProps> = ({
  validationMessage,
  dataSource,
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === 'number' ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Vul het veld in: ${title}`,
            },
            { validator: checkLabelExist(record, validationMessage) },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

export const TableTranslations = forwardRef(
  (
    props: {
      dataSource?: DbTranslationKeyValue[];
      safeData: Function;
    },
    ref,
  ) => {
    const { i18n, t } = useTranslation();

    const [form] = Form.useForm();
    const [data, setData] = useState<Array<Item>>([]);
    const [dataTemp, setTempData] = useState<Array<Item>>([]);
    const [editingKey, setEditingKey] = useState('');

    const isEditing = (record: Item) => record.key === editingKey;

    useImperativeHandle(ref, () => ({
      SetSearchText(text: string) {
        if (text.length == 0) {
          setData([...dataTemp]);
          return;
        }

        var filteredData = dataTemp.filter((x) => {
          return (
            x.languageKey.toLocaleLowerCase().includes(text) ||
            x.languageLabel.toLocaleLowerCase().includes(text)
          );
        });
        setData(filteredData);
      },
    }));

    useEffect(() => {
      if (props.dataSource != undefined) {
        setData(InitData(props.dataSource));
        setTempData(InitData(props.dataSource));
      }
    }, [props.dataSource]);

    const InitData = (items: DbTranslationKeyValue[]) => {
      var newItems: Item[] = [];
      for (var i = 0; i < items.length; i++) {
        newItems.push({
          key: i.toString(),
          index: i,
          languageKey: items[i].languageKey,
          languageLabel: items[i].languageLabel,
        });
      }
      return newItems;
    };

    const editRecord = (record: Partial<Item> & { key: React.Key }) => {
      form.setFieldsValue({ ...record });
      setEditingKey(record.key);
    };

    const deleteRecord = (key: React.Key) => {
      const newData = dataTemp.filter((item) => item.key !== key);
      setData(newData);
      setTempData(newData);
      props.safeData(newData);
    };

    const cancelEditRecord = () => {
      setEditingKey('');
    };

    const addRecord = () => {
      setEditingKey('');

      const newData: Item = {
        key: (Math.random() + 1).toString(36).substring(7),
        index: dataTemp.length,
        languageKey: `newKey${dataTemp.length}`,
        languageLabel: `newLabel${dataTemp.length}`,
      };
      setEditingKey(newData.key);
      setData([...data, newData]);
    };

    const saveRecord = async (record: Item) => {
      try {
        const row = (await form.validateFields()) as Item;

        console.log('row', row);
        console.log('record', record);

        row.index = record.index;

        const newData = [...dataTemp];
        const index = newData.findIndex((item) => record.index === item.index);

        console.log('index', index);
        console.log('newData', newData);

        if (index >= 0) {
          const item = newData[index];
          newData.splice(index, 1, {
            ...item,
            ...row,
          });
          setData(newData);
          setTempData(newData);
          setEditingKey('');
        } else {
          newData.push(row);
          setData(newData);
          setTempData(newData);
          setEditingKey('');
        }

        props.safeData(newData);
      } catch (errInfo) {
        console.log('Validate Failed:', errInfo);
      }
    };

    const SafeAllDataAfterDrag = (indexFirst: number, indexSecond: number) => {
      var newData = [...data];
      var tmp = newData[indexSecond];
      newData[indexSecond] = newData[indexFirst];
      newData[indexFirst] = tmp;
      props.safeData(newData);
    };

    const columns = [
      {
        key: 'sort',
      },
      {
        title: t('languageKey'),
        dataIndex: 'languageKey',
        width: '40%',
        editable: true,
      },
      {
        title: t('languageLabel'),
        dataIndex: 'languageLabel',
        width: '35%',
        editable: true,
      },
      {
        title: t('operation'),
        dataIndex: 'operation',
        width: '200px',
        editable: false,
        render: (_: any, record: Item) => {
          const editable = isEditing(record);
          return editable ? (
            <span style={{ display: 'flex', flexDirection: 'row' }}>
              <Typography.Link
                onClick={() => saveRecord(record)}
                style={{ marginRight: 8 }}
              >
                {t('safe')}
              </Typography.Link>
              <Typography.Link onClick={() => cancelEditRecord()}>
                {t('cancel')}
              </Typography.Link>
            </span>
          ) : (
            <>
              <Typography.Link
                onClick={() => editRecord(record)}
                style={{ marginRight: 8 }}
              >
                {t('edit')}
              </Typography.Link>
              <Typography.Link onClick={() => deleteRecord(record.key)}>
                {t('delete')}
              </Typography.Link>
            </>
          );
        },
      },
    ];

    const onDragEnd = ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        setData((previous) => {
          const activeIndex = previous.findIndex((i) => i.key === active.id);
          const overIndex = previous.findIndex((i) => i.key === over?.id);
          SafeAllDataAfterDrag(activeIndex, overIndex);

          return arrayMove(previous, activeIndex, overIndex);
        });
      }
    };

    const Row = ({ children, ...props }: RowProps) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({
        id: props['data-row-key'],
      });

      const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Transform.toString(
          transform && { ...transform, scaleY: 1 },
        ),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
      };

      return (
        <tr {...props} ref={setNodeRef} style={style} {...attributes}>
          {React.Children.map(children, (child) => {
            if ((child as React.ReactElement).key === 'sort') {
              return React.cloneElement(
                child as React.ReactElement,
                {},
                <MenuOutlined
                  ref={setActivatorNodeRef}
                  style={{ touchAction: 'none', cursor: 'move' }}
                  {...listeners}
                />
              );
            }
            return child;
          })}
        </tr>
      );
    };

    const mergedColumns = columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: Item) => ({
          validationMessage: t('keyAlreadyExist'),
          record,
          inputType: col.dataIndex === 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

    return (
      <Form form={form} component={false}>
        <div
          style={{
            zIndex: '',
          }}
        >
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={data.map((i) => i.key)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                locale={{ emptyText: t('noData') }}
                components={{
                  body: {
                    row: Row,
                    cell: EditableCell,
                  },
                }}
                size="small"
                dataSource={data}
                columns={mergedColumns}
                rowClassName="editable-row"
                scroll={{ y: 400, scrollToFirstRowOnChange: true }}
              />
            </SortableContext>
          </DndContext>
          <Button
            icon={<PlusCircleOutlined />}
            disabled={editingKey == '' ? false : true}
            onClick={addRecord}
            type="primary"
            style={{ marginBottom: 16 }}
          >
            {t('addARow')}
          </Button>
        </div>
      </Form>
    );
  },
);
